import React, { useEffect, useState } from "react";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
} from "chart.js";
import { TrendingUp, Users, Globe, Activity } from "lucide-react";
import { API_BASE_URL } from "../../config/apiConfig";

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, Title, Tooltip, Legend);



const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
        y: { beginAtZero: true, grid: { color: "rgba(156, 163, 175, 0.1)" } },
        x: { grid: { display: false } },
    },
};

// Function to get country from IP using ip-api.com (free, no key required)
const getCountryFromIP = async (ip) => {
    try {
        // Skip local IPs
        if (ip === "127.0.0.1" || ip === "::1" || ip.startsWith("192.168.") || ip.startsWith("10.")) {
            return "Local";
        }

        const response = await fetch(`http://ip-api.com/json/${ip}`);
        const data = await response.json();

        if (data.status === "success") {
            return data.country || "Unknown";
        }
        return "Unknown";
    } catch (error) {
        console.error(`Error fetching country for IP ${ip}:`, error);
        return "Unknown";
    }
};

// Batch process IPs with delay to respect rate limits
const getCountriesFromIPs = async (visits) => {
    const countryMap = {};
    const uniqueIPs = [...new Set(visits.map(v => v.ip_address).filter(Boolean))];

    for (let i = 0; i < uniqueIPs.length; i++) {
        const ip = uniqueIPs[i];
        const country = await getCountryFromIP(ip);
        countryMap[ip] = country;

        // Add delay to respect rate limits (45 requests per minute for ip-api.com)
        if (i < uniqueIPs.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1500));
        }
    }

    return countryMap;
};

export default function VisitsDashboard() {
    const [totalVisitors, setTotalVisitors] = useState(0);
    const [visitsData, setVisitsData] = useState({ labels: [], counts: [] });
    const [loading, setLoading] = useState(true);
    const [todayVisits, setTodayVisits] = useState(0);
    const [averageDaily, setAverageDaily] = useState(0);
    const [allVisits, setAllVisits] = useState([]);
    const [countryData, setCountryData] = useState({ labels: [], counts: [] });
    const [geoLoading, setGeoLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem("token");

                // Fetch total visitors
                const resTotal = await fetch(`${API_BASE_URL}/api/visits/count`, {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    }
                });
                const dataTotal = await resTotal.json();
                setTotalVisitors(dataTotal.totalVisitors);

                // Fetch visits per day
                const resVisits = await fetch(`${API_BASE_URL}/api/visits/per-day`, {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    }
                });
                const dataVisits = await resVisits.json();

                const labels = dataVisits.map(item => {
                    const date = new Date(item.date);
                    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                });
                const counts = dataVisits.map(item => item.count);
                setVisitsData({ labels, counts });

                const todayStr = new Date().toISOString().split("T")[0];
                const todayData = dataVisits.find(item => item.date === todayStr);
                setTodayVisits(todayData ? todayData.count : 0);

                const total = counts.reduce((a, b) => a + b, 0);
                setAverageDaily(counts.length > 0 ? Math.round(total / counts.length) : 0);

                // Fetch all visits
                const resAll = await fetch(`${API_BASE_URL}/api/visits`, {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                });

                const dataAll = await resAll.json();
                console.log("All visits:", dataAll);
                setAllVisits(dataAll);

                // Process countries from IPs
                setGeoLoading(true);
                const countryMap = await getCountriesFromIPs(dataAll);

                // Count visits per country
                const countryCountMap = {};
                dataAll.forEach(visit => {
                    const country = countryMap[visit.ip_address] || "Unknown";
                    countryCountMap[country] = (countryCountMap[country] || 0) + 1;
                });

                // Sort by count and take top 5
                const sortedCountries = Object.entries(countryCountMap)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 5);

                setCountryData({
                    labels: sortedCountries.map(([country]) => country),
                    counts: sortedCountries.map(([, count]) => count),
                });

                setGeoLoading(false);

            } catch (err) {
                console.error("Failed to fetch data:", err);
                setGeoLoading(false);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const barChartData = {
        labels: visitsData.labels,
        datasets: [
            {
                label: "Daily Visits",
                data: visitsData.counts,
                backgroundColor: "rgba(59, 130, 246, 0.8)",
                borderColor: "rgba(59, 130, 246, 1)",
                borderWidth: 1,
                borderRadius: 6,
            },
        ],
    };

    const lineChartData = {
        labels: visitsData.labels,
        datasets: [
            {
                label: "Trend",
                data: visitsData.counts,
                borderColor: "rgba(16, 185, 129, 1)",
                backgroundColor: "rgba(16, 185, 129, 0.1)",
                fill: true,
                tension: 0.4,
                pointRadius: 4,
                pointHoverRadius: 6,
            },
        ],
    };

    const countryChartData = {
        labels: countryData.labels,
        datasets: [
            {
                data: countryData.counts,
                backgroundColor: [
                    "rgba(239, 68, 68, 0.8)",
                    "rgba(59, 130, 246, 0.8)",
                    "rgba(16, 185, 129, 0.8)",
                    "rgba(245, 158, 11, 0.8)",
                    "rgba(139, 92, 246, 0.8)",
                ],
                borderWidth: 2,
                borderColor: "#fff",
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
            y: { beginAtZero: true, grid: { color: "rgba(156, 163, 175, 0.1)" } },
            x: { grid: { display: false } },
        },
    };

    const doughnutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: "bottom", labels: { padding: 15, font: { size: 12 } } } },
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
                <div className="text-center">
                    <Activity className="w-12 h-12 text-blue-500 animate-pulse mx-auto mb-4" />
                    <p className="text-slate-600 text-lg">Loading analytics...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-slate-800 mb-2">Analytics Dashboard</h1>
                    <p className="text-slate-600">Track your website performance and visitor insights</p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-3 bg-blue-100 rounded-lg">
                                <Users className="w-6 h-6 text-blue-600" />
                            </div>
                        </div>
                        <h3 className="text-slate-600 text-sm font-medium mb-1">Total Visitors</h3>
                        <p className="text-3xl font-bold text-slate-800">{totalVisitors.toLocaleString()}</p>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-3 bg-green-100 rounded-lg">
                                <TrendingUp className="w-6 h-6 text-green-600" />
                            </div>
                        </div>
                        <h3 className="text-slate-600 text-sm font-medium mb-1">Today's Visits</h3>
                        <p className="text-3xl font-bold text-slate-800">{todayVisits}</p>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-3 bg-purple-100 rounded-lg">
                                <Activity className="w-6 h-6 text-purple-600" />
                            </div>
                        </div>
                        <h3 className="text-slate-600 text-sm font-medium mb-1">Daily Average</h3>
                        <p className="text-3xl font-bold text-slate-800">{averageDaily}</p>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-3 bg-orange-100 rounded-lg">
                                <Globe className="w-6 h-6 text-orange-600" />
                            </div>
                        </div>
                        <h3 className="text-slate-600 text-sm font-medium mb-1">Global Reach</h3>
                        <p className="text-3xl font-bold text-slate-800">
                            {geoLoading ? "..." : `${countryData.labels.length} countries`}
                        </p>
                    </div>
                </div>

                {/* Charts Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                    {/* Bar Chart */}
                    <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6 border border-slate-200">
                        <h2 className="text-xl font-bold text-slate-800 mb-4">Daily Visits Overview</h2>
                        <div className="h-80">
                            <Bar data={barChartData} options={chartOptions} />
                        </div>
                    </div>

                    {/* Doughnut Chart */}
                    <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
                        <h2 className="text-xl font-bold text-slate-800 mb-4">Top Countries</h2>
                        <div className="h-80">
                            {geoLoading ? (
                                <div className="flex items-center justify-center h-full">
                                    <div className="text-center">
                                        <Activity className="w-8 h-8 text-blue-500 animate-pulse mx-auto mb-2" />
                                        <p className="text-slate-600 text-sm">Loading geo data...</p>
                                    </div>
                                </div>
                            ) : (
                                <Doughnut data={countryChartData} options={doughnutOptions} />
                            )}
                        </div>
                    </div>
                </div>

                {/* Line Chart */}
                <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
                    <h2 className="text-xl font-bold text-slate-800 mb-4">Visitor Trend</h2>
                    <div className="h-80">
                        <Line data={lineChartData} options={lineChartOptions} />
                    </div>
                </div>
            </div>
        </div>
    );
}